import { AppBar, Box, CssBaseline, IconButton, Stack, Toolbar, Typography, useTheme, Button, Paper, TextField, Grid2 as Grid, Container } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import MenuBottomNavigation from "@/components/MenuBottomNavigation";
import MenuSidebarNavigation from "@/components/MenuSidebarNavigation";
import { useAppDispatch, useAppSelector, useResponsive } from "@/hooks";
import { userSelector, logOut } from "@/features/auth/slice";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon, Logout01Icon, SmartPhone01Icon } from "@hugeicons/core-free-icons";
import useRoutes from "@/hooks/useRoutes";
import { useUpdateUserMutation } from "@/api/usersApi";
import useSnackbar from "@/hooks/useSnackbar";
import { stringToColor, getInitials } from "@/utils/names";
import { Avatar } from "@mui/material";
import { openPhoneLoginDrawer } from "@/store/uiSlice";
import PhoneLoginDrawer from "@/components/loyalty/PhoneLoginDrawer";

const validationSchema = Yup.object({
  name: Yup.string().max(50, "Muy Largo!").required("Campo requerido"),
  lastname: Yup.string().max(50, "Muy Largo!").required("Campo requerido"),
});

const CustomerProfile = () => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [goToRoute] = useRoutes();
  const user = useAppSelector(userSelector);
  const [updateUser] = useUpdateUserMutation();
  const [openSnackbar] = useSnackbar();

  const handleGoBack = () => {
    goToRoute("organizations.menu.main");
  };

  const handleLogout = () => {
    dispatch(logOut());
    goToRoute("organizations.root");
  };

  const handleOpenLogin = () => {
    dispatch(openPhoneLoginDrawer());
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {!isMobile && <MenuSidebarNavigation />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? "100%" : "calc(100% - 280px)",
          ml: isMobile ? 0 : "280px",
          backgroundColor: "#F4F7FE",
          minHeight: "100vh",
          pb: 12,
          color: "text.primary",
        }}
      >
        {isMobile && (
          <AppBar elevation={1} color="default" position="sticky" sx={{ backgroundColor: "white" }}>
            <Toolbar>
              <Stack direction="row" flex={1} gap={2} alignItems="center">
                <IconButton onClick={handleGoBack}>
                  <HugeiconsIcon icon={ArrowLeft02Icon} color={theme.palette.primary.main} size={32} />
                </IconButton>
                <Typography variant="h4" component="div" sx={{ flexGrow: 1 }} color={"black"}>
                  Mi Perfil
                </Typography>
              </Stack>
            </Toolbar>
          </AppBar>
        )}
        {user ? (
          <>
            <CssBaseline />


            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Stack alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 120,
                          height: 120,
                          fontSize: "3rem",
                          backgroundColor: stringToColor(`${user?.name} ${user?.lastname}`),
                        }}
                        src={user?.image}
                      >
                        {getInitials(`${user?.name ?? ""} ${user?.lastname ?? ""}`)}
                      </Avatar>
                      <Box>
                        <Typography variant="h3">
                          {user.name} {user.lastname}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {user.email || user.phone}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <Stack spacing={3}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h4" mb={3}>
                        Información Personal
                      </Typography>
                      <Formik
                        initialValues={{
                          name: user.name ?? "",
                          lastname: user.lastname ?? "",
                          phone: user.phone ?? "",
                          email: user.email ?? "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (values) => {
                          try {
                            await updateUser({
                              draft: {
                                uid: user.uid,
                                name: values.name,
                                lastname: values.lastname,
                              },
                            }).unwrap();
                            openSnackbar("Perfil actualizado correctamente", "success");
                          } catch (error) {
                            openSnackbar("Error al actualizar el perfil", "error");
                          }
                        }}
                      >
                        {({ values, errors, touched, handleChange, handleSubmit, handleBlur, isSubmitting }) => (
                          <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <TextField
                                  fullWidth
                                  label="Nombre"
                                  name="name"
                                  value={values.name}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.name && Boolean(errors.name)}
                                  helperText={touched.name && errors.name}
                                />
                                <TextField
                                  fullWidth
                                  label="Apellido"
                                  name="lastname"
                                  value={values.lastname}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.lastname && Boolean(errors.lastname)}
                                  helperText={touched.lastname && errors.lastname}
                                />
                              </Stack>

                              <TextField
                                fullWidth
                                label="Teléfono"
                                name="phone"
                                value={values.phone}
                                disabled
                                helperText="El teléfono no puede ser modificado"
                              />

                              <TextField
                                fullWidth
                                label="Correo Electrónico"
                                name="email"
                                value={values.email}
                                disabled
                              />

                              <Box>
                                <Button
                                  type="submit"
                                  variant="contained"
                                  size="large"
                                  disabled={isSubmitting}
                                >
                                  Guardar Cambios
                                </Button>
                              </Box>
                            </Stack>
                          </form>
                        )}
                      </Formik>
                    </Paper>

                    <Paper sx={{ p: 3 }}>
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "start", md: "center" }} spacing={{ xs: 2, md: 0 }}>
                        <Box>
                          <Typography variant="h4">Sesión</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cierra tu sesión en este dispositivo
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<HugeiconsIcon icon={Logout01Icon} />}
                          onClick={handleLogout}
                        >
                          Cerrar Sesión
                        </Button>
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
            </Container>


          </>

        ) : (
          <Stack height={'calc(100vh - 128px)'} spacing={2} justifyContent={"center"} alignItems={"center"}>
            <Typography variant="h2" color="text.primary">
              Inicia sesión
            </Typography>
            <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ maxWidth: 300, mb: 2 }}>
              Inicia sesión con tu número de teléfono para ver tu información.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<HugeiconsIcon icon={SmartPhone01Icon} />}
              onClick={handleOpenLogin}
              sx={{ borderRadius: 4, px: 4, py: 1.5 }}
            >
              Entrar con mi Teléfono
            </Button>
          </Stack>
        )}
        <PhoneLoginDrawer />
      </Box>
      {isMobile && <MenuBottomNavigation />}
    </Box>
  );
};


export default CustomerProfile;

